/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import CDSHeaderName from '@carbon/web-components/es/components/ui-shell/header-name.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './masthead.scss';
import C4DLeftNav from './left-nav';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The brand name UI in left nav.
 *
 * @element c4d-left-nav-name
 * @csspart side-nav-submenu - The side navigation submenu. Usage: `c4d-left-nav-name::part(side-nav-submenu)`
 * @csspart side-nav-name-prefix - The prefix for the side navigation name. Usage: `c4d-left-nav-name::part(side-nav-name-prefix)`
 */
@customElement(`${c4dPrefix}-left-nav-name`)
class C4DLeftNavName extends CDSHeaderName {
  /**
   * Set slot name property.
   */
  @property({ type: String, attribute: 'slot', reflect: true })
  slot = '';

  connectedCallback() {
    super.connectedCallback();
    if (this.parentElement instanceof C4DLeftNav) {
      this.slot = 'platform-id';
    }
  }

  render() {
    const { href, prefix: namePrefix } = this;
    const namePrefixPart = !namePrefix
      ? undefined
      : html`
          <span
            class="${prefix}--header__name--prefix"
            part="side-nav-name-prefix"
            >${namePrefix}</span
          >&nbsp;
        `;
    return html`
      <a
        part="side-nav-submenu"
        class="${prefix}--side-nav__submenu ${prefix}--side-nav__submenu-platform"
        href="${ifDefined(href)}">
        ${namePrefixPart}
        <slot></slot>
      </a>
    `;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DLeftNavName;
